import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  additionalCharges as initialMockCharges,
  type AdditionalCharge,
} from '@/mocks/additionalCharges';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<AdditionalCharge[]> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.97) reject(new Error('Timeout al cargar catálogo.'));
      else resolve([...initialMockCharges]);
    }, 400 + Math.random() * 200);
  });
}

export default function AdminAdditionalCharges() {
  const [charges, setCharges] = useState<AdditionalCharge[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((d) => {
        setCharges(d.filter((c) => c.active));
        setLoadingState('ready');
      })
      .catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">

        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/admin/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Cargos Adicionales</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Cargos Adicionales</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${charges.length} servicios disponibles para contratar`
                    : 'Cargando catálogo...'}
                </p>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 self-start sm:self-auto">
                <i className="ri-information-line text-[9px]"></i>Solo lectura
              </span>
            </div>

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="animate-pulse p-4 space-y-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 py-2">
                      <div className="w-8 h-8 rounded-lg bg-background-200"></div>
                      <div className="flex-1 space-y-1.5">
                        <div className="w-36 h-3 bg-background-200 rounded"></div>
                        <div className="w-60 h-2 bg-background-200 rounded"></div>
                      </div>
                      <div className="w-16 h-6 bg-background-200 rounded-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar datos</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm"></i>Reintentar
                </button>
              </div>
            )}

            {/* Table */}
            {loadingState === 'ready' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-background-200 bg-background-100/50">
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Servicio</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Descripción</th>
                        <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Precio</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {charges.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-4 py-12 text-center">
                            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                              <i className="ri-stack-line text-foreground-300 text-xl"></i>
                            </div>
                            <p className="text-foreground-500 text-sm font-semibold">Sin cargos disponibles</p>
                            <p className="text-foreground-400 text-xs mt-1">La plataforma no tiene cargos adicionales activos en este momento.</p>
                          </td>
                        </tr>
                      )}
                      {charges.map((charge) => (
                        <tr key={charge.id} className="hover:bg-background-100/60 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-amber-50 text-amber-600">
                                <i className="ri-stack-fill text-sm"></i>
                              </div>
                              <p className="text-foreground-800 text-xs font-semibold truncate">{charge.name}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 max-w-[400px]">
                            <p className="text-foreground-500 text-[10px] leading-relaxed line-clamp-2">{charge.description}</p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-foreground-800 text-sm font-bold font-mono">
                              ${charge.price.toFixed(2)}
                            </span>
                            <span className="text-foreground-400 text-[9px] ml-1">USD</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile footer */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Cargos Adicionales</p>
      </div>
    </div>
  );
}